import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send, ArrowLeft, Mail, Download, ChevronDown, ChevronUp } from 'lucide-react';
import { AppShell } from '../components/Layout';
import { usePatient } from '../contexts/PatientContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function parseStage(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed)) {
      if (typeof parsed[0] === 'string') {
        return parsed.map(e => {
          const [s, c] = e.split(':').map(x => x.trim());
          return { Stage: s || 'Unknown', Confidence: parseFloat(c) || 0 };
        });
      }
      return parsed;
    }
    if (typeof parsed === 'object')
      return Object.entries(parsed).map(([s, c]) => ({ Stage: s, Confidence: parseFloat(c) || 0 }));
    return [];
  } catch { return []; }
}

function safeJSON(val) {
  if (!val) return [];
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return []; }
}

// ─── Plain-text body for mailto (mail apps handle plain text best) ─────────────
function buildPlainText({ patientName, patientId, date, time, analysisId, imgUrl, reportList, woundTissue, stageList, adviceList, message }) {
  const divider = '─'.repeat(40);

  const reportSection = reportList?.length
    ? reportList.map(r => `  • ${r['Analysis Type'] || r.analysisType || 'N/A'}: ${r['Result'] || r.result || 'N/A'}`).join('\n')
    : '  No analysis data.';

  const tissueEntries = woundTissue?.length && typeof woundTissue[0] === 'object' ? Object.entries(woundTissue[0]) : [];
  const tissueSection = tissueEntries.length
    ? tissueEntries.map(([k, v]) => `  • ${k}: ${parseFloat(v).toFixed(2)}%`).join('\n')
    : '  No tissue data.';

  const stageSection = stageList?.length
    ? stageList.map(s => `  • ${s.Stage || 'N/A'}: ${s.Confidence != null ? Number(s.Confidence).toFixed(2) : 'N/A'}%`).join('\n')
    : '  No stage data.';

  const adviceSection = adviceList?.length
    ? adviceList.map(a => `  • ${a}`).join('\n')
    : '';

  return [
    'CYTOMETRIX WOUND ANALYSIS REPORT',
    `Generated: ${new Date().toLocaleString()}`,
    '',
    ...(message ? [`NOTE FROM SENDER\n${divider}\n${message}`, ''] : []),
    'PATIENT INFORMATION',
    divider,
    `Patient Name : ${patientName || 'N/A'}`,
    `Patient ID   : ${patientId   || 'N/A'}`,
    `Date         : ${date        || 'N/A'}`,
    `Time         : ${time        || 'N/A'}`,
    `Analysis ID  : ${analysisId  || 'N/A'}`,
    `Image URL    : ${imgUrl      || 'N/A'}`,
    '',
    'ANALYSIS SUMMARY',
    divider,
    reportSection,
    '',
    'WOUND TISSUE COMPOSITION',
    divider,
    tissueSection,
    '',
    'WOUND STAGE CLASSIFICATION',
    divider,
    stageSection,
    ...(adviceSection ? ['', 'CARE ADVICE', divider, adviceSection] : []),
    '',
    divider,
    'Shared via Cytometrix Wound Analysis App',
    'This report is a clinical adjunct and does not replace professional judgment.',
  ].join('\n');
}

// ─── HTML report (download only) ──────────────────────────────────────────────
function buildHtml({ patientName, patientId, date, time, analysisId, imgUrl, reportList, woundTissue, stageList, adviceList, message }) {
  const rows = (list, headers, rowFn) => `
    <table style="width:100%;border-collapse:collapse;margin-top:10px;border:1px solid #CBD5E1;">
      <thead><tr>${headers.map(h => `<th style="padding:10px;background:#E2E8F0;font-weight:600;color:#0F172A;text-align:left;border-bottom:1px solid #CBD5E1;">${h}</th>`).join('')}</tr></thead>
      <tbody>${list.map(rowFn).join('')}</tbody>
    </table>`;
  const td = v => `<td style="padding:10px;border-bottom:1px solid #E2E8F0;font-size:14px;color:#334155;">${v ?? 'N/A'}</td>`;
  const tr = cells => `<tr>${cells.map(td).join('')}</tr>`;

  const reportRows = reportList?.length
    ? rows(reportList, ['Analysis Type', 'Result'], r => tr([r['Analysis Type'] || r.analysisType || 'N/A', r['Result'] || r.result || 'N/A']))
    : '<p style="color:#64748B;font-style:italic;">No analysis data.</p>';

  const tissueEntries = woundTissue?.length && typeof woundTissue[0] === 'object' ? Object.entries(woundTissue[0]) : [];
  const tissueRows = tissueEntries.length
    ? rows(tissueEntries, ['Tissue Type', 'Percentage (%)'], ([k, v]) => tr([k, parseFloat(v).toFixed(2)]))
    : '<p style="color:#64748B;font-style:italic;">No tissue data.</p>';

  const stageRows = stageList?.length
    ? rows(stageList, ['Stage', 'Confidence (%)'], item => tr([item.Stage || 'N/A', item.Confidence != null ? Number(item.Confidence).toFixed(2) : 'N/A']))
    : '<p style="color:#64748B;font-style:italic;">No stage data.</p>';

  const adviceRows = adviceList?.length
    ? rows(adviceList, ['Care Advice'], text => tr([text]))
    : '';

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Wound Analysis Report</title></head>
<body style="font-family:Arial,sans-serif;color:#334155;margin:32px;background:#fff;">
  <div style="border-bottom:3px solid #1B6CA8;padding-bottom:20px;margin-bottom:24px;">
    <h1 style="color:#0A1628;font-size:22px;margin:0 0 4px;">Cytometrix Wound Analysis Report</h1>
    <p style="color:#7A95B0;font-size:13px;margin:0;">Generated on ${new Date().toLocaleString()}</p>
  </div>
  ${message ? `<div style="background:#EFF6FF;border-left:4px solid #1B6CA8;padding:14px;margin-bottom:24px;border-radius:4px;"><p style="margin:0;color:#1e40af;">${message}</p></div>` : ''}
  <h2 style="color:#1E293B;font-size:16px;">Patient Information</h2>
  <table style="width:100%;border-collapse:collapse;margin-top:10px;border:1px solid #CBD5E1;">
    <tbody>
      <tr><th style="padding:10px;background:#E2E8F0;text-align:left;width:30%;">Patient Name</th><td style="padding:10px;border-bottom:1px solid #E2E8F0;">${patientName || 'N/A'}</td><th style="padding:10px;background:#E2E8F0;text-align:left;width:20%;">Patient ID</th><td style="padding:10px;border-bottom:1px solid #E2E8F0;">${patientId || 'N/A'}</td></tr>
      <tr><th style="padding:10px;background:#E2E8F0;text-align:left;">Date</th><td style="padding:10px;border-bottom:1px solid #E2E8F0;">${date || 'N/A'}</td><th style="padding:10px;background:#E2E8F0;text-align:left;">Time</th><td style="padding:10px;">${time || 'N/A'}</td></tr>
      <tr><th style="padding:10px;background:#E2E8F0;text-align:left;">Analysis ID</th><td style="padding:10px;border-bottom:1px solid #E2E8F0;">${analysisId || 'N/A'}</td><th style="padding:10px;background:#E2E8F0;text-align:left;">Image URL</th><td style="padding:10px;">${imgUrl || 'N/A'}</td></tr>
    </tbody>
  </table>
  <h2 style="color:#1E293B;font-size:16px;margin-top:28px;">Analysis Summary</h2>${reportRows}
  <h2 style="color:#1E293B;font-size:16px;margin-top:28px;">Wound Tissue Composition</h2>${tissueRows}
  <h2 style="color:#1E293B;font-size:16px;margin-top:28px;">Wound Stage Classification</h2>${stageRows}
  ${adviceRows ? `<h2 style="color:#1E293B;font-size:16px;margin-top:28px;">Care Advice</h2>${adviceRows}` : ''}
  <p style="margin-top:36px;font-size:12px;color:#94A3B8;border-top:1px solid #E2E8F0;padding-top:16px;">
    Shared via Cytometrix Wound Analysis Web App · This report is a clinical adjunct and does not replace professional judgment.
  </p>
</body></html>`;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  grid:        { display: 'grid', gridTemplateColumns: '1fr', gap: 16 },
  gridDesktop: { gridTemplateColumns: '1fr 300px', gap: 24 },
  formGroup:   { marginBottom: 14 },
  label: {
    display: 'block',
    fontSize: '0.78rem',
    fontWeight: 600,
    marginBottom: 5,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  // fontSize 1rem prevents iOS Safari from zooming in on focus
  input: {
    width: '100%',
    minHeight: 44,
    fontSize: '1rem',
    padding: '10px 12px',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    fontSize: '1rem',
    padding: '10px 12px',
    boxSizing: 'border-box',
    resize: 'vertical',
  },
  btnRow: { display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 },
  btn: {
    flex: 1,
    minWidth: 130,
    minHeight: 44,        // touch target
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  infoBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '11px 13px',
    borderRadius: 'var(--radius-sm)',
    marginBottom: 14,
    fontSize: '0.84rem',
    lineHeight: 1.5,
    background: '#EFF6FF',
    color: '#1e40af',
    border: '1px solid #BFDBFE',
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    padding: '11px 13px',
    borderRadius: 'var(--radius-sm)',
    marginBottom: 14,
    fontSize: '0.84rem',
    lineHeight: 1.5,
    background: '#FEF2F2',
    color: '#991B1B',
    border: '1px solid #FECACA',
  },
  previewSection: {
    marginBottom: 10,
    padding: 11,
    background: 'var(--surface)',
    borderRadius: 'var(--radius-sm)',
  },
  previewLabel: {
    fontWeight: 600,
    fontSize: '0.70rem',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'var(--text-muted)',
    marginBottom: 5,
  },
  checkRow: { display: 'flex', gap: 8, marginBottom: 3, fontSize: '0.83rem' },
};

// ─── useIsDesktop ─────────────────────────────────────────────────────────────
function useIsDesktop(breakpoint = 768) {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= breakpoint);
  React.useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isDesktop;
}

// ─── ShareForm ────────────────────────────────────────────────────────────────
function ShareForm({ patientName, patientId, date, time, analysisId, imgUrl, reportList, woundTissue, stageList, adviceList }) {
  const [recipient,   setRecipient]   = useState('');
  const [subject,     setSubject]     = useState('Wound Analysis Report – Cytometrix');
  const [message,     setMessage]     = useState('');
  const [emailError,  setEmailError]  = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const isDesktop = useIsDesktop();

  const reportData = useMemo(() => ({
    patientName, patientId, date, time, analysisId, imgUrl,
    reportList, woundTissue, stageList, adviceList,
  }), [patientName, patientId, date, time, analysisId, imgUrl, reportList, woundTissue, stageList, adviceList]);

  // Opens Outlook / Apple Mail / Gmail app — whatever is default on the device
  const handleOpenMailApp = (e) => {
    e.preventDefault();
    setEmailError('');

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipient)) {
      setEmailError('Please enter a valid email address.');
      return;
    }

    const body   = buildPlainText({ ...reportData, message });
    const mailto =
      `mailto:${encodeURIComponent(recipient)}` +
      `?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;

    window.location.href = mailto;
  };

  const handleDownload = () => {
    const html = buildHtml({ ...reportData, message });
    const blob = new Blob([html], { type: 'text/html' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `cytometrix-report-${patientId || 'patient'}-${Date.now()}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const previewSections = [
    'Examination Info',
    'Analysis Summary',
    'Tissue Composition',
    'Wound Stage',
    ...(adviceList?.length ? ['Care Advice'] : []),
  ];

  return (
    <div style={isDesktop ? { ...S.grid, ...S.gridDesktop } : S.grid}>

      {/* ── Form card ── */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">
            <div className="card-icon"><Mail size={16} /></div>
            Open in Mail App
          </div>
        </div>

        <div style={S.infoBanner}>
          <Mail size={15} style={{ marginTop: 1, flexShrink: 0 }} />
          <span>
            Tapping <strong>Open Mail App</strong> launches Outlook, Apple Mail, or your device's
            default email app with the report pre-filled and ready to send.
          </span>
        </div>

        {emailError && (
          <div style={S.errorBanner}>⚠ {emailError}</div>
        )}

        <form onSubmit={handleOpenMailApp} noValidate>
          <div style={S.formGroup}>
            <label style={S.label}>Recipient Email *</label>
            <input
              className="form-control"
              style={S.input}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="colleague@hospital.ca"
              value={recipient}
              onChange={e => { setRecipient(e.target.value); setEmailError(''); }}
              required
            />
          </div>

          <div style={S.formGroup}>
            <label style={S.label}>Subject</label>
            <input
              className="form-control"
              style={S.input}
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
            />
          </div>

          <div style={S.formGroup}>
            <label style={S.label}>Optional Message</label>
            <textarea
              className="form-control"
              style={{ ...S.textarea, minHeight: 96 }}
              rows={4}
              placeholder="Add a note for the recipient…"
              value={message}
              onChange={e => setMessage(e.target.value)}
            />
          </div>

          <div style={S.btnRow}>
            <button
              type="button"
              className="btn btn-secondary"
              style={S.btn}
              onClick={handleDownload}
            >
              <Download size={15} />
              Download HTML
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              style={S.btn}
            >
              <Send size={15} />
              Open Mail App
            </button>
          </div>
        </form>
      </div>

      {/* ── Preview card (collapsible on mobile) ── */}
      <div className="card" style={{ alignSelf: 'start' }}>
        <button
          type="button"
          onClick={() => !isDesktop && setPreviewOpen(p => !p)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: isDesktop ? 'default' : 'pointer',
            marginBottom: (isDesktop || previewOpen) ? 12 : 0,
          }}
        >
          <span className="card-title" style={{ fontSize: '0.9rem' }}>Report Preview</span>
          {!isDesktop && (previewOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
        </button>

        {(isDesktop || previewOpen) && (
          <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
            <div style={S.previewSection}>
              <div style={S.previewLabel}>Patient</div>
              <div style={{ fontWeight: 500 }}>{patientName || 'N/A'}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>{patientId || 'N/A'}</div>
            </div>

            <div style={S.previewSection}>
              <div style={S.previewLabel}>Date &amp; Time</div>
              <div>{date || 'N/A'}{time ? ` · ${time}` : ''}</div>
            </div>

            <div style={S.previewSection}>
              <div style={S.previewLabel}>Report includes</div>
              {previewSections.map(s => (
                <div key={s} style={S.checkRow}>
                  <span style={{ color: 'var(--teal)', fontWeight: 700 }}>✓</span> {s}
                </div>
              ))}
            </div>

            <p style={{ fontSize: '0.71rem', color: 'var(--text-muted)', margin: '6px 0 0', lineHeight: 1.5 }}>
              The report is sent as plain text in the mail app body. Download the HTML version for a fully formatted copy.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Screens ──────────────────────────────────────────────────────────────────
export function ShareResultScreen() {
  const navigate    = useNavigate();
  const location    = useLocation();
  const { patient } = usePatient();
  const { result }  = location.state || {};
  const now         = new Date();

  const reportList  = result?.Report || result?.Result?.Report || [];
  const woundTissue = result?.['Wound Tissue (%)'] || result?.Result?.['Wound Tissue (%)'] || [];
  const stageList   = useMemo(() => {
    const raw = result?.Stage || result?.Result?.Stage;
    if (!raw) return [];
    if (Array.isArray(raw) && raw[0]?.Stage) return raw;
    if (typeof raw === 'object') return Object.entries(raw).map(([s, c]) => ({ Stage: s, Confidence: c }));
    return [];
  }, [result]);
  const adviceList  = Array.isArray(result?.Advice || result?.Result?.Advice)
    ? (result?.Advice || result?.Result?.Advice) : [];
  const imgUrl      = String(result?.Img_URL || result?.Result?.Img_URL || 'N/A');
  const analysisId  = result?.AnalysisID || result?.Result?.AnalysisID || 'N/A';

  return (
    <AppShell title="Share Result">
      <div style={S.pageHeader}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Back
        </button>
        <div>
          <h1 className="page-title" style={{ marginBottom: 2 }}>Share Analysis Report</h1>
          <p className="page-subtitle">Send wound analysis results to a colleague or specialist</p>
        </div>
      </div>
      <ShareForm
        patientName={patient?.full_name}
        patientId={patient?.patient_id}
        date={now.toLocaleDateString()}
        time={now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        analysisId={analysisId}
        imgUrl={imgUrl}
        reportList={reportList}
        woundTissue={woundTissue}
        stageList={stageList}
        adviceList={adviceList}
      />
    </AppShell>
  );
}

export function ShareHistoryScreen() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { result } = location.state || {};

  const date        = useMemo(() => result?.timestamp ? new Date(result.timestamp).toLocaleDateString() : '', [result]);
  const time        = useMemo(() => result?.timestamp ? new Date(result.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '', [result]);
  const reportList  = useMemo(() => safeJSON(result?.Analysis), [result]);
  const woundTissue = useMemo(() => safeJSON(result?.Tissue),   [result]);
  const stageList   = useMemo(() => parseStage(result?.Stage),  [result]);

  return (
    <AppShell title="Share History">
      <div style={S.pageHeader}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Back
        </button>
        <div>
          <h1 className="page-title" style={{ marginBottom: 2 }}>Share Visit Record</h1>
          <p className="page-subtitle">Send historical wound analysis to a colleague</p>
        </div>
      </div>
      <ShareForm
        patientName={result?.full_name}
        patientId={result?.patient_id}
        date={date}
        time={time}
        analysisId={result?.AnalysisID}
        imgUrl={result?.Img_URL}
        reportList={reportList}
        woundTissue={woundTissue}
        stageList={stageList}
        adviceList={[]}
      />
    </AppShell>
  );
}
